// src/public-routes/sectors/B2BSector.jsx
import React, { useEffect } from 'react';
import { Container, Row, Col, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import {
    FaBuilding,
    FaSearch,
    FaChartLine,
    FaUsers,
    FaHandshake,
    FaCogs,
    FaTrophy,
    FaFilter
} from 'react-icons/fa';

const B2BSector = () => {
    useEffect(() => {
        window.scrollTo(0, 0);

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const stats = [
        { number: '67%', label: 'of B2B buyers start their journey online' },
        { number: '3.4x', label: 'higher conversion with targeted account-based marketing' },
        { number: '84%', label: 'of B2B decision-makers are influenced by digital content' },
        { number: '45%', label: 'reduction in cost-per-lead with optimized campaigns' }
    ];

    const approaches = [
        {
            title: "Account-Based Marketing",
            description: "Target high-value accounts with personalized campaigns that engage key decision-makers and accelerate pipeline velocity.",
            icon: FaHandshake
        },
        {
            title: "Lead Generation & Nurturing",
            description: "Build predictable lead generation engines with targeted content, paid media, and marketing automation that delivers qualified opportunities.",
            icon: FaFilter
        },
        {
            title: "Content & Thought Leadership",
            description: "Establish authority in your industry with strategic content that educates, builds trust, and positions your brand as the go-to expert.",
            icon: FaChartLine
        },
        {
            title: "Sales & Marketing Alignment",
            description: "Bridge the gap between marketing and sales with shared data, aligned processes, and integrated technology that drives revenue growth.",
            icon: FaCogs
        }
    ];

    const benefits = [
        ['Higher Quality Leads', 'Attract decision-makers actively searching for your solutions with intent-driven marketing'],
        ['Shorter Sales Cycles', 'Accelerate pipeline velocity with targeted content that addresses specific buyer needs'],
        ['Increased Deal Size', 'Engage multiple stakeholders effectively and demonstrate enterprise value'],
        ['Better ROI Measurement', 'Track and optimize every marketing dollar with advanced B2B attribution'],
        ['Sustainable Pipeline Growth', 'Build a predictable, scalable lead generation engine that compounds over time']
    ];

    const questions = [
        'How can you simplify complex B2B buying journeys?',
        'What strategies will generate high-quality, conversion-ready leads?',
        'How do you engage multiple decision-makers in the buying process?',
        'Which channels deliver the best ROI for B2B lead generation?',
        'How can you align marketing and sales for maximum revenue impact?'
    ];

    return (
        <div className="b2b-sector-page st-page">
            {/* Top Image Section */}
            <div className="st-decorative-image-container">
                <img
                    src={require('./B2B.PNG')}
                    alt="B2B Digital Marketing - Lead Generation and Pipeline Growth"
                    loading="eager"
                />
            </div>

            {/* Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">B2B</span> Digital Marketing
                    </h1>
                    <p>Simplify complex B2B journeys with targeted campaigns, intelligent lead generation, and data-backed decision making</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    In the complex <span className="sv-keyword-highlight">B2B landscape</span>, we simplify intricate buying journeys with targeted campaigns, intelligent lead generation, and data-backed decision making that drives quality opportunities.
                                </p>
                                <p className="sv-description">
                                    At Reachtix, our <strong>B2B digital marketing services</strong> combine strategic thinking, data intelligence, and advanced tools to create campaigns that engage decision-makers and accelerate pipeline velocity. Our focus is not just on generating leads — but on driving high-quality, conversion-ready opportunities.
                                </p>
                                <p className="sv-description">
                                    We understand the unique challenges B2B companies face: long sales cycles, multiple stakeholders, complex buying processes, and the need to demonstrate clear ROI while building lasting business relationships.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button tag={Link} to="/contact" color="dark">
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start B2B Strategy
                                    </Button>
                                    <Button tag={Link} to="/contact" color="dark">
                                        <i className="fas fa-comments mr-2"></i>
                                        Get Free Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            Why <span className="sv-text-gradient">B2B Marketing</span> Matters
                        </h2>
                        <Row className="sv-stats-grid">
                            {stats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* What We Do Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What We Do for B2B Companies</h2>
                                <p className="sv-section-description">
                                    <strong>B2B digital marketing</strong> goes beyond just generating leads. It's about creating intelligent, data-driven strategies that engage the right decision-makers at the right time with the right message, while building a predictable pipeline that drives sustainable revenue growth.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Questions We Answer:</h4>
                                    {questions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Overview */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Invest in B2B Marketing?</h2>
                                <p className="sv-section-description">
                                    B2B buying behavior has fundamentally changed. Today's business buyers expect personalized, relevant experiences throughout their journey. A comprehensive digital marketing strategy is essential to meet these expectations and drive measurable business results.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-filter"></i>
                                        <h5>Quality Leads</h5>
                                        <p>Generate conversion-ready opportunities with intent-driven, targeted campaigns</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-clock"></i>
                                        <h5>Faster Sales Cycles</h5>
                                        <p>Accelerate pipeline with content that addresses buyer needs at every stage</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-handshake"></i>
                                        <h5>Stakeholder Engagement</h5>
                                        <p>Reach and influence multiple decision-makers effectively</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-bar"></i>
                                        <h5>Measurable ROI</h5>
                                        <p>Track and optimize every marketing dollar with advanced attribution</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Approach Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our B2B Marketing Services</h2>
                                <p className="sv-section-description">
                                    Reachtix offers a comprehensive suite of B2B marketing services designed to simplify complex journeys, generate qualified opportunities, and drive measurable pipeline growth.
                                </p>

                                <Row className="sv-services-grid">
                                    {approaches.map((service, index) => (
                                        <Col lg={6} md={6} key={index} className="mb-4">
                                            <div className="sv-approach-card p-4 h-100">
                                                <div className="sv-approach-icon">
                                                    <service.icon />
                                                </div>
                                                <div className="sv-approach-content">
                                                    <h4>{service.title}</h4>
                                                    <p className="service-description">{service.description}</p>
                                                </div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Table Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Key Benefits of B2B Marketing With Reachtix</h2>
                                <div className="table-responsive">
                                    <table className="table sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Core Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Main CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Transform Your B2B Pipeline?</h2>
                                <p className="sv-cta-description">
                                    Whether you're a startup seeking product-market fit or an established enterprise scaling your sales engine, our comprehensive B2B marketing services provide the expertise and results you need to drive predictable growth.
                                </p>
                                <p className="sv-cta-description">
                                    Let's build a lead generation engine that delivers qualified opportunities and accelerates your pipeline.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button tag={Link} to="/contact" color="primary">
                                        <i className="fas fa-calendar-check mr-2"></i>
                                        Start B2B Consultation
                                    </Button>
                                    <Button tag={Link} to="/contact" color="light">
                                        <i className="fas fa-clipboard-list mr-2"></i>
                                        Get Pipeline Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default B2BSector;